import React, { FC, useState, useEffect, useRef } from "react";
import { Button, Form, FormInstance } from "antd";
import {
  PricingLevel,
  PricingType,
  PricingTarget,
  MatterType,
  MatterFormMode,
  MatterNonExternalFieldsPreUpdateMatterCreate,
  OrganisationalUnit,
  ExternalMatter,
  RelevantMatter,
} from "../../data-types";
import { RateReplacement } from "~/gql/graphql";
import { formValuesToData } from "../matter-creation-or-editing-form";
import { MatterFormValuesToData } from "../matter-creation-or-editing-form/types";
import { pricingStructureToCascaderValue } from "../pricing-structure-options/pricing-structure-options";
import styles from "./create-proposal.css";
import { MatterTitleScreenTabKey } from "./relevant-matter-tabs";

const {
  ["button-container"]: buttonContainerClassName,
  ["bounce-animation"]: bounceAnimationClassName,
} = styles;

export const orgUnitCascaderFromValue = (ou: OrganisationalUnit) => {
  if (!ou?.parent?.id) {
    return [ou?.id];
  }
  return [ou?.parent?.id, ou?.id];
};

export enum TitleInputState {
  CENTERED, // Input is centered (initial state)
  TOP_WITH_ANIMATION, // Input moves to top with animation (when user enters title)
  TOP_WITHOUT_ANIMATION, // Input stays at top without animation (when returning from details)
}

export type CreateProposalProps = {
  loading?: boolean;
  onCreate: (matter: {
    title: string;
    externalReference?: string;
    clientId: number | null;
    pricingLevel?: PricingLevel;
    pricingType?: PricingType;
    templateId?: number;
    practiceAreaId: number;
    multiDisciplinaryMatter?: boolean;
    rateReplacements?: RateReplacement[];
  }) => void;
  createMultiDiscMatter: (matter: { title: string; clientId: number; phases: string[] }) => void;
  onCancel?: () => void;
  parentMatterId?: number;
  selectedMatter: RelevantMatter | null;
  setSelectedMatter: (matter: RelevantMatter | null) => void;
  selectedExternalMatter: ExternalMatter | null;
  setSelectedExternalMatter: (matter: ExternalMatter | null) => void;
  matterTitleScreen: (
    form: FormInstance,
    options: {
      uiState: TitleInputState;
      setUiState: (state: TitleInputState) => void;
      resetForm: () => void;
    }
  ) => React.ReactNode;
  matterDetailsScreen: (
    form: FormInstance,
    setScreen: (screen: CreateProposalScreens) => void
  ) => React.ReactNode;
  deprecatedRatesScreen?: (
    form: FormInstance,
    setScreen: (screen: CreateProposalScreens) => void,
    setRateReplacements: (replacements: RateReplacement[]) => void,
    setAllRatesReplaced: (allReplaced: boolean) => void
  ) => React.ReactNode;
  hasDeprecatedRates: boolean;
  setActiveTab: (tab: MatterTitleScreenTabKey) => void;
};

export enum CreateProposalScreens {
  MatterTitle = 0,
  MatterDetails = 1,
  DeprecatedRates = 2,
}

const isFirstScreen = (screen: CreateProposalScreens) =>
  screen === CreateProposalScreens.MatterTitle;

const isLastScreen = (screen: CreateProposalScreens, hasDeprecatedRates: boolean) =>
  screen === CreateProposalScreens.DeprecatedRates ||
  (screen === CreateProposalScreens.MatterDetails && !hasDeprecatedRates);

const getNextScreen = (
  currentScreen: CreateProposalScreens,
  hasDeprecatedRates: boolean
): CreateProposalScreens => {
  if (currentScreen === CreateProposalScreens.MatterTitle) {
    return CreateProposalScreens.MatterDetails;
  } else if (currentScreen === CreateProposalScreens.MatterDetails && hasDeprecatedRates) {
    return CreateProposalScreens.DeprecatedRates;
  }
  return currentScreen;
};

export const CreateProposal: FC<CreateProposalProps> = ({
  loading,
  onCreate,
  createMultiDiscMatter,
  onCancel,
  parentMatterId,
  selectedMatter,
  setSelectedMatter,
  selectedExternalMatter,
  setSelectedExternalMatter,
  matterTitleScreen,
  matterDetailsScreen,
  deprecatedRatesScreen,
  hasDeprecatedRates,
  setActiveTab,
}) => {
  const [screen, setScreen] = useState(CreateProposalScreens.MatterTitle);
  const [form] = Form.useForm();
  const [isButtonAnimating, setIsButtonAnimating] = useState(false);
  const [titleInputState, setTitleInputState] = useState<TitleInputState>(TitleInputState.CENTERED);
  const [isCreating, setIsCreating] = useState(false);
  const [rateReplacements, setRateReplacements] = useState<RateReplacement[]>([]);
  const [allRatesReplaced, setAllRatesReplaced] = useState(false);

  const resetState = () => {
    form.resetFields();
    setSelectedMatter(null);
    setSelectedExternalMatter(null);
    setScreen(CreateProposalScreens.MatterTitle);
  };

  const resetForm = () => {
    form.setFieldValue("title", "");
  };

  useEffect(() => {
    if (selectedMatter) {
      if (!selectedMatter.template) {
        form.setFieldsValue({
          copyScope: true,
          copyResourcing: true,
          copyPricing: true,
          pricingStructure: pricingStructureToCascaderValue({
            pricingType: selectedMatter.pricingType,
            pricingLevel: selectedMatter.pricingLevel,
            pricingTarget: selectedMatter.pricingTarget,
          }),
        });
      } else {
        form.setFieldsValue({
          copyScope: false,
          copyResourcing: false,
          copyPricing: false,
          pricingStructure: null,
        });
      }
    }
  }, [selectedMatter]);

  useEffect(() => {
    if (selectedMatter) {
      const timer = setTimeout(() => {
        setIsButtonAnimating(true);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setIsButtonAnimating(false);
    }
  }, [selectedMatter]);

  const screenComponent = isCreating
    ? null
    : {
        [CreateProposalScreens.MatterTitle]: matterTitleScreen(form, {
          uiState: titleInputState,
          setUiState: setTitleInputState,
          resetForm,
        }),
        [CreateProposalScreens.MatterDetails]: matterDetailsScreen(form, setScreen),
        [CreateProposalScreens.DeprecatedRates]: deprecatedRatesScreen
          ? deprecatedRatesScreen(form, setScreen, setRateReplacements, setAllRatesReplaced)
          : null,
      }[screen] || null;

  const handleCancel = () => {
    if (isCreating) return;

    onCancel?.();

    setTimeout(() => {
      resetState();
      resetForm();
      setActiveTab(MatterTitleScreenTabKey.TemplateMatters);
      setTitleInputState(TitleInputState.CENTERED);
    }, 0);
  };

  const handleBack = () => {
    if (isCreating) return;

    form.setFieldsValue({
      practiceAreaId: null,
      pricingStructure: null,
      copyScope: false,
      copyResourcing: false,
      copyPricing: false,
    });
    setSelectedMatter(null);
    setSelectedExternalMatter(null);
    setScreen(CreateProposalScreens.MatterTitle);
  };

  const handleReturnToTitleScreen = () => {
    if (isCreating) return;

    const currentTitle = form.getFieldValue("title");
    const newTitleState =
      !currentTitle || currentTitle.trim() === ""
        ? TitleInputState.CENTERED
        : TitleInputState.TOP_WITHOUT_ANIMATION;

    Promise.resolve().then(() => {
      handleBack();
      setScreen(CreateProposalScreens.MatterTitle);
      setTitleInputState(newTitleState);
    });
  };

  const handleNextScreen = () => {
    if (isCreating) return;

    form
      .validateFields()
      .then(() => {
        setScreen(getNextScreen(screen, hasDeprecatedRates));
      })
      .catch((e) => {
        console.log("error", { e });
      });
  };

  const handleCreate = () => {
    if (isCreating) return;

    form
      .validateFields()
      .then(() => {
        setIsCreating(true);

        const values = form.getFieldsValue(true);
        const convertedValues = convertToCreateMatter(values);

        // Add rate replacements if we have them
        if (rateReplacements.length > 0 && selectedMatter && !selectedMatter.template) {
          onCreate({
            ...convertedValues,
            rateReplacements,
          });
        } else {
          onCreate(convertedValues);
        }
      })
      .catch((e) => {
        console.log("error", { e });
      });
  };

  const lastValidScreenComponentRef = useRef<React.ReactNode>(null);
  useEffect(() => {
    if (!isCreating && screenComponent !== null) {
      lastValidScreenComponentRef.current = screenComponent;
    }
  }, [screenComponent, isCreating]);

  const buttonGroup = (
    <>
      {isLastScreen(screen, hasDeprecatedRates) ? (
        <Button
          onClick={handleReturnToTitleScreen}
          style={{ marginRight: "8px" }}
          disabled={isCreating}
        >
          Back
        </Button>
      ) : selectedMatter || selectedExternalMatter ? (
        <Button onClick={() => handleBack()} style={{ marginRight: "8px" }} disabled={isCreating}>
          Back
        </Button>
      ) : titleInputState !== TitleInputState.CENTERED ? (
        <Button onClick={handleCancel} style={{ marginRight: "8px" }} disabled={isCreating}>
          Cancel
        </Button>
      ) : null}
      {isFirstScreen(screen) && (
        <span className={buttonContainerClassName}>
          <Button
            type="primary"
            onClick={() => handleNextScreen()}
            className={isButtonAnimating ? bounceAnimationClassName : ""}
            onAnimationEnd={() => setIsButtonAnimating(false)}
            disabled={isCreating}
          >
            {selectedMatter
              ? selectedMatter.template
                ? "Use this template"
                : "Use this historical matter"
              : "Create from scratch"}
          </Button>
        </span>
      )}
      {screen === CreateProposalScreens.MatterDetails && (
        <Button
          type="primary"
          onClick={hasDeprecatedRates ? handleNextScreen : handleCreate}
          loading={isCreating}
          disabled={isCreating}
        >
          {hasDeprecatedRates ? "Next" : isCreating ? "Creating..." : "Create"}
        </Button>
      )}
      {screen === CreateProposalScreens.DeprecatedRates && (
        <Button
          type="primary"
          onClick={handleCreate}
          loading={isCreating}
          disabled={isCreating || !allRatesReplaced}
        >
          {isCreating ? "Creating..." : "Create"}
        </Button>
      )}
    </>
  );

  return (
    <div
      style={{
        position: "relative",
        padding: "24px",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "24px",
          right: "24px",
          zIndex: 1000,
          background: "white",
          padding: "8px",
          borderRadius: "4px",
        }}
      >
        {buttonGroup}
      </div>
      <div style={{ opacity: isCreating ? 0.7 : 1, pointerEvents: isCreating ? "none" : "auto" }}>
        {isCreating ? lastValidScreenComponentRef.current : screenComponent}
      </div>
    </div>
  );
};

function convertToCreateMatter<T extends MatterType, U extends MatterFormMode>(
  props: MatterFormValuesToData<T, U>
) {
  const convertedValues = formValuesToData(props, "matter" as MatterType, MatterFormMode.Create);
  return convertedValues as MatterNonExternalFieldsPreUpdateMatterCreate;
}
